import React, { Component } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { AlertTriangle } from 'react-feather';
import { isCurrentSection } from 'selectors/editor';
import Modal from 'components/shared/modal';
import { Icon, Spinner } from 'ui';
import sectionActions, { sectionOrder } from '../../../actions/editor';
import DropTarget from './drop_target';
import DragSource from './drag_source';

class EditorSection extends Component {
  constructor(props) {
    super(props);
    this.state = { hover: false, loading: false };
    this.showDeleteConfirmation = this.showDeleteConfirmation.bind(this);
    this.closeDeleteConfirmation = this.closeDeleteConfirmation.bind(this);
  }

  get deleteModalConfig() {
    return {
      type: 'confirm',
      show: this.state.showConfirmModal,
      title: 'Delete Section',
      message: 'Are you sure you want to delete this section?',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      onConfirm: this.props.delete,
      onCancel: this.closeDeleteConfirmation,
      dismissCallback: this.closeDeleteConfirmation,
    };
  }

  get sectionTitle() {
    if (this.props.isCommenting) return this.props.position;
    return this.props.title || 'Untitled Section';
  }

  get hasInvalidElements() {
    return this.props.sectionsWithInvalidElements.some(id => {
      return id === this.props.id;
    });
  }

  get showSectionControls() {
    return (
      !this.props.isCommenting &&
      !this.state.loading &&
      !this.props.templateActive
    );
  }

  closeDeleteConfirmation() {
    this.setState({ showConfirmModal: false });
  }

  showDeleteConfirmation() {
    this.setState({ showConfirmModal: true });
  }

  duplicateSection = () => {
    this.setState({ loading: true }, () => {
      this.props
        .duplicate()
        .then(() => {
          this.setLoadingState(false);
        })
        .catch(e => {
          this.setLoadingState(false);
          console.error(e);
        });
    });
  };

  setLoadingState = value => {
    this.setState({ loading: value });
  };

  renderChildren = () => {
    return (
      <div className="d-f">
        {this.hasInvalidElements && (
          <AlertTriangle size={18} className="mr-8 fs-0" />
        )}
        {this.sectionTitle}
      </div>
    );
  };

  render() {
    const styleNames = classNames('EditorSections-section', {
      'EditorSections-section--active': this.props.active,
      'EditorSections-section--hover': this.state.hover,
      'EditorSections-section--isDragging': this.props.isDragging,
    });
    const { isCommenting } = this.props;

    return compose(
      this.props.connectDragSource,
      this.props.connectDragPreview,
      this.props.connectDropTarget,
    )(
      <div
        className={styleNames}
        onMouseOver={() => !isCommenting && this.setState({ hover: true })}
        onMouseOut={() => !isCommenting && this.setState({ hover: false })}
        onFocus={() => {
          return 0;
        }}
        onBlur={() => {
          return 0;
        }}
      >
        <Modal {...this.deleteModalConfig} />
        <section
          className="EditorSections-sectionTitle undecorated"
          onClick={!this.props.active ? this.props.edit : null}
          children={this.renderChildren()}
          onKeyDown={e =>
            e.key === 'Enter' && (!this.props.active ? this.props.edit : null)
          }
        />
        {this.showSectionControls && (
          <div
            className="EditorSections-sectionControls"
            key={this.props.position}
          >
            {!this.props.is_scorm_section && (
              <div
                className="EditorSections-controlButton"
                onClick={this.duplicateSection}
                onKeyDown={e => e.key === 'Enter' && this.duplicateSection}
              >
                <Icon name="copy" />
              </div>
            )}
            <div
              className="EditorSections-controlButton"
              onClick={this.showDeleteConfirmation}
              onKeyDown={e => e.key === 'Enter' && this.showDeleteConfirmation}
            >
              <Icon name="delete" />
            </div>
            <div className="EditorSections-controlButton EditorSections-controlButton--draggable">
              <Icon name="reorder" />
            </div>
          </div>
        )}
        {!this.props.isCommenting && this.state.loading && (
          <div
            className="EditorSections-sectionControls"
            key={this.props.position}
          >
            <Spinner />
          </div>
        )}
      </div>,
    );
  }
}

const mapStateToProps = () => {
  const isActive = isCurrentSection();
  return (state, ownProps) => ({
    active: isActive(state.editor, ownProps.id),
    isCommenting: state.editor.commentary.visible,
    sectionsWithInvalidElements: state.editor.sectionsWithInvalidElements,
    templateActive: state.editor.templateActive,
  });
};

const mapDispatchToProps = () => (dispatch, ownProps) => ({
  delete(e) {
    e.preventDefault();
    dispatch(sectionActions.delete(ownProps.id));
  },
  edit(e) {
    e.preventDefault();
    dispatch(sectionActions.edit(ownProps.id));
  },
  order(ownIndex, overIndex) {
    dispatch(sectionOrder(ownIndex, overIndex));
  },
  update(changes) {
    dispatch(sectionActions.update(ownProps.id, changes));
  },
  duplicate() {
    return dispatch(sectionActions.duplicate(ownProps.id));
  },
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  DropTarget,
  DragSource,
)(EditorSection);
